import { useState, FormEvent } from 'react';
import { sendEmail, EMAILJS_CONFIG, isEmailJSConfigured } from '../../../utils/emailjs';

export function AppointmentBooking() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    preferredDate: '',
    preferredTime: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if (!isEmailJSConfigured()) {
        console.warn('EmailJS is not configured. Please update src/utils/emailjs.ts with your credentials.');
        setSubmitStatus('error');
        return;
      }

      await sendEmail(EMAILJS_CONFIG.APPOINTMENT_TEMPLATE_ID, {
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        service: formData.service,
        message: formData.message,
      });

      setSubmitStatus('success');
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        preferredDate: '',
        preferredTime: '',
        service: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 bg-gradient-to-br from-teal-50 via-cyan-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 10 Q35 15 30 20 Q25 15 30 10\' fill=\'%2314B8A6\'/%3E%3C/svg%3E")' }}></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Book Your Appointment
            </h2>
            <p className="text-gray-600 mb-8">We'll confirm within 2 hours</p>

            <form id="appointment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Date *</label>
                  <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
                  <select
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  >
                    <option value="">Select time</option>
                    <option value="Morning (9 AM - 12 PM)">Morning (9 AM - 12 PM)</option>
                    <option value="Afternoon (12 PM - 4 PM)">Afternoon (12 PM - 4 PM)</option>
                    <option value="Evening (4 PM - 8 PM)">Evening (4 PM - 8 PM)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Service Required *</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="Cosmetic Dentistry">Cosmetic Dentistry</option>
                  <option value="Root Canal Treatment">Root Canal Treatment</option>
                  <option value="Dental Implants">Dental Implants</option>
                  <option value="Orthodontics">Orthodontics</option>
                  <option value="Teeth Whitening">Teeth Whitening</option>
                  <option value="Preventive Care">Preventive Care</option>
                  <option value="General Consultation">General Consultation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Any specific concerns or questions?"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white py-4 rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                {isSubmitting ? 'Submitting...' : 'Request Appointment'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm">
                  <strong>Success!</strong> Your appointment request has been submitted. We'll contact you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                  <strong>Error!</strong> Something went wrong. Please try again or call us directly.
                </div>
              )}

              <p className="text-center text-sm text-gray-600">
                Or call us directly at{' '}
                <a href="tel:+918340978930" className="text-teal-600 font-semibold hover:underline cursor-pointer">
                  +918340978930
                </a>
              </p>

              <a
                href="https://wa.me/918340978930?text=Hi%2C%20I%20would%20like%20to%20book%20an%20appointment%20at%20Sri%20Maruthi%20Teeth%20Empire"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-full bg-green-500 text-white py-4 rounded-xl font-semibold hover:bg-green-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Book via WhatsApp
              </a>
            </form>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20dental%20clinic%20waiting%20area%20with%20comfortable%20seating%20natural%20light%20plants%20teal%20accents%20welcoming%20reception%20desk%20professional%20clean%20contemporary%20interior&width=600&height=400&seq=clinic-interior-006&orientation=landscape"
                alt="Sri Maruthi Teeth Empire Clinic Interior Kondapur Hyderabad"
                className="w-full h-64 object-cover object-top"
              />
            </div>

            <div className="rounded-3xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.941889829743!2d78.3505021!3d17.4624952!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb93d0b79f036f%3A0x2cbe217413560d9c!2sTeeth%20Empire%20Dental%20clinic!5e0!3m2!1sen!2sin!4v1771486499099!5m2!1sen!2sin"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sri Maruthi Teeth Empire Dental Clinic Location Map Kondapur Hyderabad"
                className="w-full"
              ></iframe>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Sri Maruthi Teeth Empire Dental Clinic</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Botanical Garden Road, Kondapur<br />
                    Hyderabad, Telangana 500084
                  </p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Botanical+Garden+Road+Kondapur+Hyderabad"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 text-teal-600 font-semibold text-sm mt-3 hover:gap-3 transition-all cursor-pointer"
                  >
                    Get Directions
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Working Hours</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Monday - Sunday: 10:00 AM - 8:00 PM</p>
                    <p></p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-full flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-red-900 mb-1">Emergency Contact</div>
                    <a href="tel:+918340978930" className="text-lg font-bold text-red-600 hover:underline cursor-pointer">
                      +918340978930
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 mt-6">
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <i className="ri-parking-box-line text-2xl text-teal-600 mb-1"></i>
                  <p className="text-xs text-gray-600">Parking Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
